/**
 * InfinitSales - Mapa de capacidades por canal (lado do navegador)
 *
 * Fonte unica de "o que existe em cada inbox" para o frontend, espelho de
 * backend/services/channel_capabilities.py. Ate 2026-09-23 havia 8 mapas de
 * rotulo por canal duplicados e ~55 comparacoes `=== 'instagram'` espalhadas
 * por 19 arquivos; cada canal novo era mais um `elif` em cada um. A skill de
 * paridade manda que a decisao "este recurso existe neste canal?" venha de
 * um lugar so, e este e o lugar.
 *
 * De onde vem o mapa:
 *  - GET /api/system/channels (atras de CHANNEL_CAPABILITIES_API_ENABLED),
 *    carregado por hooks/useChannels.js e injetado aqui por setChannelsSnapshot.
 *  - Enquanto a flag esta OFF, a rede falha ou o fetch nao chegou: a copia
 *    LOCAL dos 4 canais abaixo, com os mesmos valores. Comportamento identico
 *    nos dois casos para os 4 canais de hoje; o que a API acrescenta e o 5o
 *    canal chegar sem redeploy do frontend.
 *
 * Aqui tambem mora a fiacao que e SO do frontend (rota do inbox, campo do
 * deep-link openChat, icone do lucide), que o backend nao conhece.
 *
 * Regra da casa: chave opaca ('instagram:{igsid}', 'messenger:{psid}') nunca
 * vira telefone. channelOf() decide pelo PREFIXO, e openChatState() so tira
 * digitos de chave que nao tem prefixo.
 */
import { Facebook, Globe, Instagram, MessageCircle, Smartphone } from 'lucide-react';

// Copia local do mapa do backend (so os campos que a UI usa). Ordem canonica
// igual a do backend: WhatsApp Business primeiro.
const LOCAL_ORDER = ['wa_business', 'wa_personal', 'instagram', 'messenger', 'widget'];

const LOCAL_CHANNELS = {
  wa_business: {
    label: 'WhatsApp Business', short: 'WhatsApp Business', distinct: 'WhatsApp Business',
    icon: 'message-circle', color: '#00a884',
    key_kind: 'phone', key_prefix: null, connection_kind: 'number', multi_connection: false,
    origin_selector: 'number', team_policy: 'delegation',
    window: '24h_template', template: true, interactive: 'native', catalog: true, cards: 'none',
    media: ['image', 'audio', 'video', 'document', 'sticker', 'location'], audio_in: true,
    text_limit: null, receipts: ['sent', 'delivered', 'read'],
    can_start_conversation: true, groups: false, username: false, transfer_card: true,
    quote: 'native', reactions: 'native', edit: 'none', forward: true, delete_for_everyone: 'none',
    delivery: 'immediate', enabled: true,
  },
  wa_personal: {
    label: 'WhatsApp', short: 'WhatsApp', distinct: 'WhatsApp QR',
    icon: 'smartphone', color: '#25d366',
    key_kind: 'phone', key_prefix: null, connection_kind: 'instance', multi_connection: true,
    origin_selector: 'number', team_policy: 'number',
    window: 'none', template: false, interactive: 'degrade', catalog: false, cards: 'none',
    media: ['image', 'audio', 'video', 'document', 'sticker', 'location'], audio_in: true,
    text_limit: null, receipts: ['sent', 'delivered', 'read'],
    can_start_conversation: true, groups: true, username: false, transfer_card: true,
    quote: 'native', reactions: 'native', edit: 'native', forward: true, delete_for_everyone: 'native',
    delivery: 'immediate', enabled: true,
  },
  instagram: {
    label: 'Instagram Direct', short: 'Instagram', distinct: 'Instagram',
    icon: 'instagram', color: '#c13584',
    key_kind: 'opaque', key_prefix: 'instagram:', connection_kind: 'account', multi_connection: false,
    origin_selector: null, team_policy: 'mirror',
    window: '24h', template: false, interactive: 'none', catalog: false, cards: 'none',
    media: ['image', 'audio', 'video'], audio_in: true,
    text_limit: { value: 1000, unit: 'bytes' }, receipts: ['sent', 'read'],
    can_start_conversation: false, groups: false, username: true, transfer_card: false,
    quote: 'none', reactions: 'none', edit: 'none', forward: true, delete_for_everyone: 'none',
    delivery: 'immediate', enabled: true,
  },
  messenger: {
    label: 'Facebook Messenger', short: 'Messenger', distinct: 'Messenger',
    icon: 'facebook', color: '#1877f2',
    key_kind: 'opaque', key_prefix: 'messenger:', connection_kind: 'page', multi_connection: false,
    origin_selector: null, team_policy: 'mirror',
    window: '24h', template: false, interactive: 'none', catalog: false, cards: 'none',
    media: ['image', 'audio', 'video', 'document'], audio_in: true,
    text_limit: { value: 2000, unit: 'chars' }, receipts: ['sent', 'delivered', 'read'],
    can_start_conversation: false, groups: false, username: false, transfer_card: false,
    quote: 'none', reactions: 'none', edit: 'none', forward: true, delete_for_everyone: 'none',
    delivery: 'immediate', enabled: true,
  },
  // 5o canal (fase 1, 2026-09-23). `enabled: false` na copia local: o trilho
  // e as telas do widget so nascem com a flag do backend (GET /api/widget/status
  // responde 404 sem ela); os rotulos e a rota ja resolvem.
  widget: {
    label: 'Widget do site', short: 'Widget', distinct: 'Widget',
    icon: 'globe', color: '#00a884',
    key_kind: 'opaque', key_prefix: 'widget:', connection_kind: 'widget', multi_connection: true,
    origin_selector: 'widget', team_policy: 'mirror',
    window: 'none', template: false, interactive: 'native', catalog: false, cards: 'native',
    media: ['image'], audio_in: false,
    text_limit: { value: 4000, unit: 'chars' }, receipts: ['sent', 'delivered', 'read'],
    can_start_conversation: false, groups: false, username: false, transfer_card: false,
    quote: 'none', reactions: 'none', edit: 'none', forward: true, delete_for_everyone: 'none',
    delivery: 'deferred', enabled: false,
  },
};

// Fiacao que so o frontend conhece: rota do inbox e campo do deep-link
// `location.state.openChat` que cada shell le.
const UI_WIRING = {
  wa_business: { route: '/wa-business', openChatField: 'contact_phone' },
  wa_personal: { route: '/inbox', openChatField: 'contact_phone' },
  instagram: { route: '/instagram', openChatField: 'contact_igsid' },
  messenger: { route: '/messenger', openChatField: 'contact_psid' },
  widget: { route: '/widget', openChatField: 'contact_wid' },
};

const ICONS = {
  smartphone: Smartphone,
  'message-circle': MessageCircle,
  instagram: Instagram,
  facebook: Facebook,
  globe: Globe,
};

const MEDIA_HUMANA = {
  image: 'imagem', audio: 'áudio', video: 'vídeo',
  document: 'documento', sticker: 'figurinha', location: 'localização',
};

let _channels = { ...LOCAL_CHANNELS };
let _order = [...LOCAL_ORDER];
let _fromBackend = false;

/**
 * Recebe o `data` de GET /api/system/channels ({channels, order}) e passa a
 * responder por ele. Chamado por hooks/useChannels.js. Payload invalido e
 * ignorado (fica na copia local).
 */
export function setChannelsSnapshot(data) {
  const channels = data && data.channels;
  const order = data && data.order;
  if (!channels || typeof channels !== 'object' || !Array.isArray(order) || !order.length) return false;
  const next = {};
  order.forEach((code) => {
    if (channels[code]) next[code] = { ...channels[code] };
  });
  if (!Object.keys(next).length) return false;
  _channels = next;
  _order = order.filter((code) => next[code]);
  _fromBackend = true;
  return true;
}

/** Volta a copia local (testes e logout). */
export function resetChannelsSnapshot() {
  _channels = { ...LOCAL_CHANNELS };
  _order = [...LOCAL_ORDER];
  _fromBackend = false;
}

export function channelsFromBackend() {
  return _fromBackend;
}

/** Mapa atual {code: spec}. Copia rasa: quem altera nao afeta o store. */
export function getChannels() {
  return { ..._channels };
}

/** Ordem canonica dos canais conhecidos. */
export function channelOrder() {
  return [..._order];
}

/** Spec do canal ou null. Nunca levanta. */
export function channelSpec(code) {
  return _channels[String(code || '')] || null;
}

export function isKnownChannel(code) {
  return Boolean(channelSpec(code));
}

/** Canais com a flag ligada no backend (copia local = todos). */
export function enabledChannels() {
  return _order.filter((code) => _channels[code] && _channels[code].enabled !== false);
}

export function isPhoneChannel(code) {
  const spec = channelSpec(code);
  return Boolean(spec && spec.key_kind === 'phone');
}

export function isOpaqueChannel(code) {
  const spec = channelSpec(code);
  return Boolean(spec && spec.key_kind === 'opaque');
}

/** Prefixo da chave opaca do canal ('instagram:'), ou '' para telefone. */
export function keyPrefixOf(code) {
  const spec = channelSpec(code);
  return (spec && spec.key_prefix) || '';
}

/**
 * Canal de uma chave de contato pelo PREFIXO ('instagram:123' -> 'instagram').
 * Chave sem prefixo e telefone: devolve `hint` se for um canal de telefone,
 * senao 'wa_personal' (o inbox padrao do WhatsApp). Nunca le digitos de uma
 * chave opaca.
 */
export function channelOf(key, hint) {
  const raw = String(key || '').trim().toLowerCase();
  for (const code of _order) {
    const prefix = _channels[code] && _channels[code].key_prefix;
    if (prefix && raw.startsWith(prefix)) return code;
  }
  if (hint && isPhoneChannel(hint)) return hint;
  return 'wa_personal';
}

/** A chave e de canal opaco (tem prefixo de algum canal conhecido)? */
export function isOpaqueKey(key) {
  const raw = String(key || '').trim().toLowerCase();
  return _order.some((code) => {
    const prefix = _channels[code] && _channels[code].key_prefix;
    return Boolean(prefix && raw.startsWith(prefix));
  });
}

/** Valor da chave sem o prefixo do canal (IGSID cru, PSID cru, digitos). */
export function keyValueOf(key) {
  const raw = String(key || '').trim();
  const code = channelOf(raw);
  const prefix = keyPrefixOf(code);
  if (prefix) return raw.slice(prefix.length).trim();
  return raw.replace(/\D/g, '');
}

/**
 * Rotulo do canal. `variant`: 'short' (selo: "Instagram"), 'label' (nome
 * formal: "Instagram Direct"), 'distinct' (distingue os dois WhatsApp:
 * "WhatsApp QR"). Canal desconhecido devolve o proprio codigo, para a tela
 * nunca ficar vazia.
 */
export function labelOf(code, variant = 'short') {
  const spec = channelSpec(code);
  if (!spec) return String(code || '');
  return spec[variant] || spec.short || spec.label || String(code);
}

export function colorOf(code) {
  const spec = channelSpec(code);
  return (spec && spec.color) || '#8696a0';
}

/** Componente de icone do lucide para o canal (Smartphone, Instagram...). */
export function iconOf(code) {
  const spec = channelSpec(code);
  return (spec && ICONS[spec.icon]) || MessageCircle;
}

/** Rota do inbox do canal ('/instagram'). Desconhecido: '/inbox'. */
export function routeOf(code) {
  const wiring = UI_WIRING[String(code || '')];
  return (wiring && wiring.route) || '/inbox';
}

/**
 * O `openChat` que o shell do canal le no location.state, a partir da chave
 * do contato: {contact_igsid} para o Instagram, {contact_phone} para o
 * WhatsApp... `extra` e mesclado por cima (chat_id, channel).
 */
export function openChatState(code, key, extra = {}) {
  const wiring = UI_WIRING[String(code || '')] || UI_WIRING.wa_personal;
  const raw = String(key || '').trim();
  const prefix = keyPrefixOf(code);
  const value = prefix ? raw.slice(raw.toLowerCase().startsWith(prefix) ? prefix.length : 0).trim()
    : raw.replace(/\D/g, '');
  return { [wiring.openChatField]: value, ...extra };
}

/**
 * O canal tem a capacidade `cap` com o valor `value`? Mesma semantica do
 * backend:
 *   supports('wa_business', 'template')                      -> true
 *   supports('instagram', 'window', '24h')                    -> true
 *   supports('instagram', 'media', 'document')                -> false
 *   supports('wa_personal', 'interactive', ['native','degrade']) -> true
 * Canal desconhecido: false, sempre.
 */
export function supports(code, cap, value = true) {
  const spec = channelSpec(code);
  if (!spec || !(cap in spec)) return false;
  const atual = spec[cap];
  if (Array.isArray(atual)) return atual.includes(value);
  if (Array.isArray(value)) return value.includes(atual);
  return atual === value;
}

/** "imagem, áudio e vídeo" para a lista de midia do canal. */
export function mediaHumana(code) {
  const spec = channelSpec(code);
  const nomes = ((spec && spec.media) || []).map((m) => MEDIA_HUMANA[m] || m);
  if (nomes.length <= 1) return nomes.join('');
  return `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;
}

/**
 * "WhatsApp Business", "WhatsApp" (os dois WhatsApp colapsados) ou "WhatsApp e
 * Widget": onde a capacidade existe. Base das mensagens de "so existe no...".
 */
export function whereSupported(cap, value = true) {
  // So canais LIGADOS: um canal declarado mas desligado (widget sem a flag)
  // nao entra na frase "so existe no...".
  const codes = _order.filter((code) => supports(code, cap, value) && (_channels[code] || {}).enabled !== false);
  const nomes = [];
  const temQr = codes.includes('wa_personal');
  const temBusiness = codes.includes('wa_business');
  codes.forEach((code) => {
    if (temQr && temBusiness && (code === 'wa_personal' || code === 'wa_business')) {
      if (!nomes.includes('WhatsApp')) nomes.push('WhatsApp');
      return;
    }
    nomes.push(labelOf(code, 'short'));
  });
  if (nomes.length <= 1) return nomes.join('');
  return `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;
}

const WHY = {
  template: (onde) => `Template Meta só existe no ${onde}`,
  buttons: (onde) => `Botões só existem no ${onde}`,
  list: (onde) => `Lista só existe no ${onde}`,
  catalog: (onde) => `Catálogo só existe no ${onde}`,
  // Ações de mensagem (2026-10-08): o que a API do canal não oferece.
  quote: (onde) => `Responder citando só existe no ${onde}`,
  reactions: (onde) => `Reagir com emoji só existe no ${onde}`,
  edit: (onde) => `Editar mensagem enviada só existe no ${onde}`,
  delete_for_everyone: (onde) => `Apagar para todos só existe no ${onde}`,
  document: (onde) => `Documento só pode ser enviado no ${onde}`,
  audio: (onde) => `Gravar áudio só existe no ${onde}`,
};

/**
 * Motivo para o item aparecer DESABILITADO no canal (regra da skill: item de
 * tela compartilhada nao some, fica desabilitado com o motivo a vista).
 * '' quando o canal suporta. `cap`: template | buttons | list | catalog |
 * document | quote | reactions | edit.
 */
export function whyDisabled(code, cap) {
  if (cap === 'delete_for_everyone') {
    return supports(code, 'delete_for_everyone', 'native') ? '' : WHY.delete_for_everyone(whereSupported('delete_for_everyone', 'native'));
  }
  if (cap === 'document') {
    const media = channelSpec(code)?.media || [];
    return media.includes('document') ? '' : WHY.document(channelOrder().filter((c) => (channelSpec(c)?.media || []).includes('document')).map((c) => labelOf(c, 'distinct')).join(', ') || 'WhatsApp');
  }
  if (cap === 'audio') {
    const media = channelSpec(code)?.media || [];
    return media.includes('audio') ? '' : WHY.audio(channelOrder().filter((c) => (channelSpec(c)?.media || []).includes('audio')).map((c) => labelOf(c, 'distinct')).join(', ') || 'WhatsApp');
  }
  if (cap === 'template') return supports(code, 'template') ? '' : WHY.template(whereSupported('template'));
  if (cap === 'buttons' || cap === 'list') {
    return supports(code, 'interactive', ['native', 'degrade'])
      ? '' : WHY[cap](whereSupported('interactive', ['native', 'degrade']));
  }
  if (cap === 'catalog') return supports(code, 'catalog') ? '' : WHY.catalog(whereSupported('catalog'));
  if (cap === 'quote' || cap === 'reactions' || cap === 'edit') {
    return supports(code, cap, 'native') ? '' : WHY[cap](whereSupported(cap, 'native'));
  }
  if (cap === 'document') {
    if (!isKnownChannel(code) || supports(code, 'media', 'document')) return '';
    return `O ${labelOf(code, 'short')} não aceita documento — só ${mediaHumana(code)}.`;
  }
  return '';
}

/** Canais de janela de 24h sem template (IG, MS): o wizard de campanha e as
 *  reguas avisam que so vale dentro da janela. */
export function isWindowChannel(code) {
  return supports(code, 'window', '24h');
}

/** Opcoes {value, label} para <select> de canal, na ordem canonica. */
export function channelOptions(variant = 'distinct', codes) {
  return (codes || _order).filter(isKnownChannel).map((code) => ({ value: code, label: labelOf(code, variant) }));
}

export { LOCAL_CHANNELS, LOCAL_ORDER };
